import {
  disconnectDynamicPage,
  deleteDynamicPage,
  updateDynamicPageUrl,
  updateDynamicPageSEO,
  duplicateDynamicPage,
  getDpCreationFlows,
  addBlankDynamicPage,
} from '../business-logic/dynamic-pages/dynamicPagesApi'
import { createDPErrorHandler } from '../worker/utils/createDPErrorHandler'
import createNewDynamicPageV4PanelOpener from '../panels/new-dynamic-page-panel-v4/getPanelOpener'
import { openPanel as openNewDynamicPagePanel } from '../panels/new-dynamic-page-panel-v5/openPanel'
import { openPanel as openNewDynamicPageIntroPanel } from '../panels/new-dynamic-page-intro-panel/openPanel'
import { openPanel as openDynamicEditPagePanel } from '../panels/dynamic-edit-page-panel/openPanel'
import { openEditDynamicPageUrlStructurePanel } from '../panels/edit-dynamic-page-url-structure-panel/openPanel'
import {
  getAssetUrlInWorker,
  isSinglesCollectionSchema,
  getDeveloperMode,
  getPublishingPluginPatches,
  isBlocks,
  syncRouters,
} from '@wix/wix-data-client-common'
import {
  generateCollectionId,
  DEFAULT_PUBLISHING_MODE,
} from '@wix/wix-data-client-common-standalone'
import { openPanelByController as openDynamicPageInfoPanel } from '../panels/page-info-panel/openPanel'
import openPresetScanningPanel from '../panels/preset-scanning-panel/openPanel'
import { defaultPanelBiParams } from './defaultBiParams'
import { addPreset } from './addPreset'
import { openDPsFromSinglesErrorPanel } from './openDPsFromSinglesErrorPanel'
import openConvertToDynamicPagePanelV2 from '../panels/convert-to-dynamic-page-panel-v2/openPanel'
import openFieldPropertiesPanel from '../panels/field-properties-panel/openPanel'
import openRouterHooksPanel from '../panels/router-hooks-panel/openPanel'
import { getDynamicPageType } from '../business-logic/dynamic-pages/getDynamicPageType'
import { setUpDynamicPageUrlCounter } from './dynamic-page-url-counter'
import { notifyIfInsufficientEditingPermissionsForMembers } from './notifyIfInsufficientEditingPermissionsForMembers'
import { refreshRoutersAndEmitPageLinkGenerationEvent } from './utils/refreshRoutersAndEmitPageLinkGenerationEvent'
import { saveInEditDynamicPageUrlStructurePanel } from './save-in-edit-dynamic-page-url-structure-panel'
import { fetchRouterDatasetFromPage } from '../business-logic/dynamic-pages/fetchRouterDatasetFromPage'
import { isDuplicateRouterPrefix } from '../business-logic/dynamic-pages/generateUniquePrefix'
import openDeletePagePanel from './delete-dynamic-page/openDeletePagePanel'
import { getEditor3Api } from './apiEditor3'

import type { HttpClient } from '@wix/http-client'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { WixDataApi } from '@wix/wix-data-core'
import type Experiments from '@wix/wix-experiments'
import type {
  AppApi,
  DpBiDefaults,
  DpPageType,
  DynamicPagesAppApi,
  Features,
  EDITOR_TYPE,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  Logger,
  I18n,
} from '@wix/wix-data-client-common-standalone'

type Params = {
  fedopsLogger: FedopsLogger
  i18n: I18n
  logger: Logger
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  wixData: WixDataApi
  editorType: EDITOR_TYPE
  hostname: string
  finalAppApiPromise: Promise<AppApi>
  experimentsManager: Experiments
  httpClient: HttpClient
  features: Features
}

export const apiCreator = ({
  fedopsLogger,
  i18n,
  logger,
  editorSdkProxy,
  collectionsApi,
  wixData,
  editorType,
  hostname,
  finalAppApiPromise,
  experimentsManager,
  httpClient,
  features,
}: Params): DynamicPagesAppApi => {
  const dynamicPageUrlCounter = setUpDynamicPageUrlCounter({
    editorSdkProxy,
    httpClient,
    finalAppApiPromise,
    editorType,
  })

  const getDpFlows = async ({ biDefaults }: { biDefaults: DpBiDefaults }) => {
    const appApi = await finalAppApiPromise
    const { sandboxEnabled: isSandboxEnabled } = await appApi.getDataSettings()

    const defaultDraftPublishStatus = DEFAULT_PUBLISHING_MODE
    const isDraftPublishActivatedExperimentEnabled = experimentsManager.enabled(
      'specs.wixCode.DraftPublishActivatedByDefaultV2',
    )

    const isDeveloperModeEnabled = await getDeveloperMode(editorSdkProxy)

    const withDraftPublishActivatedByDefault =
      isDraftPublishActivatedExperimentEnabled &&
      !isBlocks(editorType) &&
      !isDeveloperModeEnabled

    const pluginsPatches = withDraftPublishActivatedByDefault
      ? getPublishingPluginPatches(defaultDraftPublishStatus)
      : []

    return getDpCreationFlows({
      collectionsApi,
      editorSdkProxy,
      wixData,
      i18n,
      editorType,
      isSandboxEnabled,
      defaultDraftPublishStatus,
      pluginsPatches,
      logger,
      fedopsLogger,
      appApi,
      biDefaults,
      experimentsManager,
      httpClient,
      features,
    })
  }

  const withDPErrorHandler = <T>(fn: () => Promise<T>) =>
    createDPErrorHandler<T>({
      i18n,
      logger,
      editorSdkProxy,
    })(fn)

  const addDefaultPage =
    ({ pageKey }: { pageKey: DpPageType }) =>
    async ({
      collectionId,
      origin,
      panelName = defaultPanelBiParams.panel_name,
    }: {
      collectionId: string
      origin: string
      panelName?: string
    }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        const appApi = await finalAppApiPromise

        return appApi.addDynamicPages({
          presetId: 'blank',
          pagesToAdd: [{ key: pageKey }],
          collectionId,
          collectionName: schema.displayName,
          fromExistingCollection: true,
          biDefaults: {
            is_first_data_modal: false,
            panel_name: panelName,
            origin,
          },
        })
      })

  return {
    async saveNewDynamicPageUrl({ pageRef, collectionId, newUrl }) {
      return updateDynamicPageUrl({
        editorSdkProxy,
        collectionsApi,
        pageRef,
        collectionId,
        newUrl,
      })
    },

    async updateDynamicPageSeo({ pageRef, title, metatags }) {
      return updateDynamicPageSEO({
        editorSdkProxy,
        pageRef,
        title,
        metatags,
      })
    },

    addDynamicPages: async ({
      pagesToAdd,
      collectionId,
      collectionName,
      biDefaults,
      presetId = 'blank',
      isIndexable = true,
      fromExistingCollection = false,
    }) => {
      const dpFlows = await getDpFlows({ biDefaults })

      const params = {
        presetId,
        isIndexable,
        pagesToAdd,
        collectionId,
        collectionName,
        origin: biDefaults.origin,
      }

      return fromExistingCollection
        ? dpFlows.addDefaultDynamicPages(params)
        : dpFlows.addPreset(params)
    },

    addBlankDynamicPage: async ({ collectionId, origin, panelName }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        const biDefaults = {
          origin,
          panel_name: panelName,
          is_first_data_modal: false,
        }

        return addBlankDynamicPage({
          editorSdkProxy,
          collectionsApi,
          appApi: await finalAppApiPromise,
          experimentsManager,
          i18n,
          logger,
          fedopsLogger,
          editorType,
          collectionId,
          biDefaults,
        })
      }),

    addDefaultDynamicItemPage: addDefaultPage({
      pageKey: 'itemPage',
    }),

    addDefaultDynamicListPage: addDefaultPage({
      pageKey: 'indexPage',
    }),

    convertToDynamicAndBindComponents: async ({
      pageRef,
      collectionId,
      collectionName,
      fields = {},
      records = [],
      bindings = [],
      biDefaults,
    }) =>
      withDPErrorHandler(async () => {
        const dpFlows = await getDpFlows({
          biDefaults: { ...biDefaults, is_first_data_modal: false },
        })

        return dpFlows.convertFromScratchAndBindComponents({
          pageRef,
          collectionId,
          collectionName,
          fields,
          records,
          bindings,
          origin: biDefaults.origin,
        })
      }),

    convertStaticToDynamicFromCollection: async ({
      pageRef,
      collectionId,
      biDefaults,
    }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        const dpFlows = await getDpFlows({
          biDefaults: { ...biDefaults, is_first_data_modal: false },
        })

        return dpFlows.convertFromCollection({
          pagesToAdd: [{ key: 'itemPage', staticPageRef: pageRef }],
          collectionId,
          collectionName: schema.displayName,
          origin: biDefaults.origin,
        })
      }),

    addPresetWithGivenPages: async ({
      presetId,
      pagesToAdd,
      collectionName,
      origin,
      isFirstTimeExperience = false,
    }) =>
      withDPErrorHandler(async () => {
        const allCollections = await collectionsApi.getAll()

        const collectionId = generateCollectionId(
          collectionName,
          allCollections,
        )

        const appApi = await finalAppApiPromise
        return appApi.addDynamicPages({
          presetId,
          pagesToAdd,
          collectionId,
          collectionName,
          biDefaults: {
            origin,
            is_first_data_modal: isFirstTimeExperience,
          },
        })
      }),

    addPresetUnhandled: async ({ presetId, origin, isFirstTimeExperience }) =>
      addPreset({
        appApi: await finalAppApiPromise,
        collectionsApi,
        editorType,
        presetId,
        origin,
        isFirstTimeExperience,
        editorSdkProxy,
      }),

    addPreset: async params =>
      withDPErrorHandler(async () =>
        (await finalAppApiPromise).addPresetUnhandled(params),
      ),

    async disconnectDynamicPage({ routerRef, pageRef }) {
      try {
        return await disconnectDynamicPage({
          editorSdkProxy,
          collectionsApi,
          routerRef,
          pageRef,
          editorType,
          appApi: await finalAppApiPromise,
        })
      } catch (error) {
        const wrappedError = new Error(`disconnectDynamicPage failed`, {
          cause: error,
        })
        logger.error(wrappedError)
        throw wrappedError
      }
    },

    async deleteDynamicPage({ routerRef, pageRef }) {
      try {
        return await deleteDynamicPage({
          editorSdkProxy,
          collectionsApi,
          routerRef,
          pageRef,
          editorType,
          appApi: await finalAppApiPromise,
        })
      } catch (error) {
        const wrappedError = new Error(`deleteDynamicPage failed`, {
          cause: error,
        })
        logger.error(wrappedError)
        throw wrappedError
      }
    },

    openDeleteDynamicPagePanel: async ({ pageRef, origin }) =>
      openDeletePagePanel(
        {
          editorSdkProxy,
          logger,
          appApi: await finalAppApiPromise,
          collectionsApi,
          i18n,
          fedopsLogger,
          editorType,
        },
        { pageRef, origin },
      ),

    openDeleteDynamicPageConfirmationPanel: ({ pageTitle }) =>
      editorSdkProxy.editor.openErrorPanel({
        helpId: '35b6fd54-22ae-4251-aa8d-2db717085ea3',
        headerText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Title',
        ),
        topDescriptionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Text',
          { nameOfPage: pageTitle },
        ),
        mainActionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Button2',
        ),
        secondaryActionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Button1',
        ),
        shouldShowIllustration: false,
      }),

    openDisconnectDynamicPageConfirmationPanel: async () =>
      editorSdkProxy.editor.openErrorPanel({
        helpId: 'e1add2cd-b7d4-45fa-b20f-57a3ad2c4609',
        headerText: i18n.t(
          'Workspace_DYNAMIC_PAGES_WARNING_REMOVE_DATASET_TITLE',
        ),
        topDescriptionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Remove_Dataset_Paragraph1',
        ),
        mainActionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Remove_Dataset_Button2',
        ),
        secondaryActionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Remove_Dataset_Button1',
        ),
        illustration: await getAssetUrlInWorker(
          editorSdkProxy,
          'panel-icons/disconnect_dynamic_page.svg',
        ),
        shouldShowIllustration: false,
      }),

    async openAddDynamicPagePanelV4({ origin, isFirstTimeExperience = false }) {
      const openNewDynamicPagePanel = createNewDynamicPageV4PanelOpener({
        appApi: await finalAppApiPromise,
        hostname,
        editorSdkProxy,
        i18n,
        editorType,
        isFirstTimeExperience,
      })
      return openNewDynamicPagePanel({
        origin,
      })
    },

    async openAddDynamicPagePanelV5({
      origin,
      collectionId,
      isFirstTimeExperience = false,
      canChangeCollection = true,
    }) {
      return openNewDynamicPagePanel({
        appApi: await finalAppApiPromise,
        hostname,
        editorSdkProxy,
        editorType,
        collectionId,
        isFirstTimeExperience,
        origin,
        canChangeCollection,
      })
    },

    async openNewDynamicPageIntroPanel({ origin }) {
      return openNewDynamicPageIntroPanel({
        appApi: await finalAppApiPromise,
        hostname,
        editorSdkProxy,
        editorType,
        origin,
      })
    },

    async openDynamicPageInfoPanel({ controllerRef }) {
      return openDynamicPageInfoPanel({ editorSdkProxy }, { controllerRef })
    },

    async openDynamicPageSEOPanel({ pageRef }) {
      return editorSdkProxy.editor.openPagesPanel({
        pageRef,
        // Note for future conversion to TS: Platform documentation demands tab type "SEO", but API works correctly with "seo"
        initialSettingsTabType: 'seo',
      })
    },

    async openPresetScanningPanel({ pageRef }) {
      return openPresetScanningPanel({
        hostname,
        editorSdkProxy,
        editorType,
        pageRef,
      })
    },

    duplicateDynamicPage: async ({ pageRef, origin }) =>
      withDPErrorHandler(() =>
        duplicateDynamicPage({
          pageRef,
          origin,
          editorSdkProxy,
          httpClient,
          collectionsApi,
          logger,
          i18n,
          editorType,
          fedopsLogger,
          experimentsManager,
          appApiPromise: finalAppApiPromise,
        }),
      ),

    openConvertToDynamicPagePanelV2: async ({ pageRef, origin }) =>
      openConvertToDynamicPagePanelV2({
        editorSdkProxy,
        appApi: await finalAppApiPromise,
        hostname,
        initialData: { pageRef, origin, editorType },
      }),

    openFieldPropertiesPanel: async ({
      name,
      keyIsRequired,
      key,
      typeOptions,
      type,
      otherKeys,
      origin,
      y,
    }) =>
      openFieldPropertiesPanel({
        editorSdkProxy,
        hostname,
        i18n,
        initialData: {
          name,
          keyIsRequired,
          key,
          typeOptions,
          type,
          otherKeys,
          origin,
          editorType,
        },
        y,
      }),

    openRouterHooksPanel: async ({ routerRef }) =>
      openRouterHooksPanel(
        {
          editorSdkProxy,
          hostname,
          i18n,
        },
        {
          routerRef,
        },
      ),

    getDynamicPageType: async ({ pageRef }) =>
      getDynamicPageType({ editorSdkProxy, pageRef }),

    getDynamicPageUrlCount: async ({ pageRef }) =>
      dynamicPageUrlCounter ? dynamicPageUrlCounter.getCount(pageRef) : null,

    getAllDynamicPageUrlCounts: async () =>
      dynamicPageUrlCounter ? dynamicPageUrlCounter.getAllCounts() : {},

    addDynamicEditPage: async ({ collectionId, origin, panelName }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        const appApi = await finalAppApiPromise

        const result = await appApi.addDynamicPages({
          pagesToAdd: [{ key: 'itemPage' }],
          collectionId,
          collectionName: schema.displayName,
          biDefaults: {
            origin,
            panel_name: panelName,
            is_first_data_modal: false,
          },
          fromExistingCollection: true,
          presetId: 'edit',
          isIndexable: false,
        })

        notifyIfInsufficientEditingPermissionsForMembers({
          appApi,
          builderType: 'studio',
          collectionId,
          collectionName: schema.displayName, // Currently, this type of page is only supported for collections without a namespace.
          i18n,
          logger,
          origin: 'edit_page',
          pageType: 'edit_page',
          permissions: schema.permissions,
        })

        return result
      }),

    refreshRoutersAndEmitPageLinkGenerationEvent: async ({
      collectionId,
      pageLinkKeys,
    }) => {
      const appApi = await finalAppApiPromise
      return refreshRoutersAndEmitPageLinkGenerationEvent({
        collectionsApi,
        editorSdkProxy,
        appApi,
        collectionId,
        pageLinkKeys,
      })
    },

    async openDynamicEditPagePanel({
      collectionId,
      isFirstTimeExperience = false,
      origin,
    }) {
      let schema
      try {
        schema = await collectionsApi.get(collectionId)
      } catch (error) {
        logger.error(
          new Error(`Failed to get collection schema`, { cause: error }),
        )
        schema = null
      }

      return openDynamicEditPagePanel({
        appApi: await finalAppApiPromise,
        collectionId,
        collectionName: schema?.displayName,
        permissions: schema?.permissions,
        editorSdkProxy,
        editorType,
        hostname,
        isFirstTimeExperience,
        origin,
      })
    },

    async openEditDynamicPageUrlStructurePanel({
      pageName,
      pageRef,
      digestedSchema,
      initialUrl,
      config,
      biData,
    }) {
      return await openEditDynamicPageUrlStructurePanel({
        editorSdkProxy,
        hostname,
        initialData: {
          pageName,
          pageRef,
          digestedSchema,
          initialUrl,
          config,
          biData,
        },
      })
    },

    async saveInEditDynamicPageUrlStructurePanel({
      pageRef,
      collectionId,
      newUrl,
    }) {
      return saveInEditDynamicPageUrlStructurePanel({
        appApi: await finalAppApiPromise,
        editorSdkProxy,
        i18n,
        logger,
        httpClient,
        pageRef,
        collectionId,
        newUrl,
        collectionsApi,
      })
    },

    fetchRouterDatasetFromPage: async pageRef => {
      const routerDataset = await fetchRouterDatasetFromPage(
        editorSdkProxy,
        pageRef,
      )
      return routerDataset?.ref || null
    },

    async isDuplicateRouterPrefix({ prefix }) {
      return await isDuplicateRouterPrefix({
        editorSdkProxy,
        prefix,
        ignorePageType: true,
      })
    },

    async handleChangedRouters({ pageRefs }) {
      const appApi = await finalAppApiPromise

      syncRouters({ editorSdkProxy, httpClient, logger })
        .then(() => appApi.refreshSchemasCache())
        .catch(() => {})

      editorSdkProxy.editor.routers.refresh()
      appApi.emit('routersChanged', pageRefs)
    },

    ...getEditor3Api({
      editorSdkProxy,
      finalAppApiPromise,
      collectionsApi,
      i18n,
      fedopsLogger,
      logger,
      wixData,
      httpClient,
    }),
  }
}
